import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { History, Users, Award, ShieldCheck } from 'lucide-react';

export default function About() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('about_title', "À Propos d'ESTAD")}
        description={t('about_name_full', "École Supérieure des Techniques Avancées pour le Développement — un nom qui incarne notre ambition.")}
        breadcrumbs={[{ label: t('nav_about', 'About'), href: '/about' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-20 px-6 lg:px-12 flex flex-col lg:flex-row gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-20 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
          
          {/* History Section */}
          <section id="history" className="space-y-10">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1 space-y-8">
                <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
                  <History className="w-8 h-8 text-gold" /> {t('about_history_title', 'Notre Histoire')}
                </h2>
                <div className="prose prose-lg text-gray-600 max-w-none font-medium leading-relaxed">
                  <p>{t('about_history_text1')}</p>
                  <p>{t('about_history_text2')}</p>
                </div>
              </div>
              <div className="w-full md:w-1/3 aspect-square rounded-sm overflow-hidden shadow-2xl border-b-8 border-gold">
                <img src="/images/campus/building-02.jpg" alt="ESTAD Building" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="bg-gray-50 p-8 border-l-8 border-navy rounded-sm italic text-navy font-bold text-lg">
              {t('about_tice_highlight', 'Première école privée au Cameroun utilisant les TICE comme moyen pédagogique principal')}
            </div>
          </section>

          {/* Leadership Section */}
          <section id="leadership" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <Users className="w-8 h-8 text-gold" /> {t('about_leadership_title', 'Mot du Leadership')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {[
                { img: '/images/leadership/pca.jpg', titleKey: 'about_pca_title', nameKey: 'about_pca_name', roleKey: null, msgKey: 'about_pca_message' },
                { img: '/images/leadership/dg.jpg',  titleKey: 'about_dg_title',  nameKey: 'about_dg_name',  roleKey: 'about_dg_role', msgKey: 'about_dg_message' },
                { img: '/images/leadership/recteur.jpg', titleKey: null, nameKey: null, roleKey: null, msgKey: null }
              ].filter(l => l.titleKey).map((leader, idx) => (
                <div key={idx} className="bg-white border border-gray-100 shadow-md hover:shadow-xl transition-all group overflow-hidden rounded-sm">
                  <div className="h-48 overflow-hidden">
                    <img src={leader.img} alt={t(leader.nameKey!)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8">
                    <div className="text-gold text-[10px] font-black uppercase tracking-widest mb-2">{t(leader.titleKey!)}</div>
                    <h3 className="text-2xl font-black text-navy mb-2 group-hover:text-gold transition-colors">{t(leader.nameKey!)}</h3>
                    {leader.roleKey && <p className="text-xs text-gray-400 font-bold mb-4 uppercase tracking-widest">{t(leader.roleKey)}</p>}
                    {leader.msgKey && (
                      <p className="text-gray-600 leading-relaxed italic border-t border-gray-50 pt-4">
                        "{t(leader.msgKey)}"
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Academic Supervisors */}
          <section id="supervisors" className="space-y-8">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <ShieldCheck className="w-8 h-8 text-gold" /> {t('about_supervisors_title', 'Superviseurs Académiques')}
            </h2>
            <p className="text-gray-600 text-lg font-medium">{t('about_supervisors_subtitle')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { name: t('supervisor_ud'), desc: t('supervisor_ud_desc') },
                { name: t('supervisor_enspd'), desc: t('supervisor_enspd_desc') },
                { name: t('supervisor_ube'), desc: t('supervisor_ube_desc') },
                { name: t('supervisor_estmre'), desc: t('supervisor_estmre_desc') }
              ].map((sup, idx) => (
                <div key={idx} className="flex gap-4 p-6 bg-gray-50 rounded-sm items-center border border-gray-100 hover:border-gold transition-all">
                  <Award className="w-10 h-10 text-gold flex-shrink-0" />
                  <div>
                    <div className="text-navy font-black text-lg uppercase tracking-tight">{sup.name}</div>
                    <div className="text-sm text-gray-500 font-medium">{sup.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
